import React, { useState, useEffect } from 'react';
import { Alert, Spin, Button } from 'antd';
import { PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { usersApi, DashboardStats } from '../../api/users';
import { PageHeader, StatCard, StatRow, StatusDot } from '../../components/shared';
import { L } from '../../styles/theme';

dayjs.locale('ru');

const STATUS_META: Record<string, { label: string; color: string }> = {
  submitted:   { label: 'Новые',       color: L.warn },
  resolved:    { label: 'Завершённые', color: L.ok },
  new:         { label: 'Новые',       color: L.warn },
  accepted:    { label: 'Принятые',    color: L.violet },
  in_progress: { label: 'В работе',    color: L.accent },
  done:        { label: 'Завершённые', color: L.ok },
  rated:       { label: 'Оценённые',   color: L.teal },
};

const Block: React.FC<{ title: string; extra?: React.ReactNode; children: React.ReactNode }> = ({
  title, extra, children,
}) => (
  <div style={{ border: `1px solid ${L.line}`, borderRadius: 10, background: L.surface, overflow: 'hidden', boxShadow: L.shadow }}>
    <div style={{
      display: 'flex', alignItems: 'center', padding: '10px 16px',
      borderBottom: `1px solid ${L.lineSoft}`,
    }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: L.ink }}>{title}</span>
      <span style={{ marginLeft: 'auto' }}>{extra}</span>
    </div>
    {children}
  </div>
);

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await usersApi.getStats();
        setStats(res.data);
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Не удалось загрузить данные дашборда');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const totalAppeals = stats?.total_appeals ?? 0;
  const appealsByStatus = stats?.appeals_by_status ?? {};
  const recentAppeals = stats?.recent_appeals ?? [];

  const statusEntries = Object.entries(appealsByStatus).filter(([, count]) => count > 0);
  const maxCount = statusEntries.reduce((m, [, c]) => Math.max(m, c), 0);

  return (
    <div>
      <PageHeader
        title="Дашборд"
        subtitle={dayjs().format('dddd, D MMMM')}
        extra={
          <>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/news/create')}>
              Новость
            </Button>
            <Button icon={<ThunderboltOutlined />} onClick={() => navigate('/shutoffs')}>
              Объявить отключение
            </Button>
          </>
        }
      />

      {error && (
        <Alert
          type="warning"
          message="Данные частично недоступны"
          description={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={loading}>
        <StatRow>
          <StatCard
            label="Жители в приложении"
            value={stats?.total_users ?? 0}
            onClick={() => navigate('/users')}
            hint={
              (stats?.new_users_week ?? 0) > 0
                ? <span><span style={{ color: L.ok }}>+{stats?.new_users_week}</span> за неделю</span>
                : 'без изменений за неделю'
            }
          />
          <StatCard
            label="Обращения"
            value={stats?.total_appeals ?? 0}
            onClick={() => navigate('/appeals')}
            hint={
              (stats?.open_appeals ?? 0) > 0
                ? <span style={{ color: L.warn }}>{stats?.open_appeals} в обработке</span>
                : 'все обработаны'
            }
          />
          <StatCard
            label="Новостей опубликовано"
            value={stats?.news_published ?? 0}
            onClick={() => navigate('/news')}
          />
          <StatCard
            label="Активные отключения"
            value={stats?.active_shutoffs ?? 0}
            color={L.danger}
            colorValue={(stats?.active_shutoffs ?? 0) > 0}
            onClick={() => navigate('/shutoffs')}
            hint={
              (stats?.active_shutoffs ?? 0) === 0
                ? <span style={{ color: L.ok }}>всё в норме</span>
                : 'жители уведомлены push'
            }
          />
        </StatRow>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 336px', gap: 16, alignItems: 'start' }}>
          <Block
            title="Последние обращения"
            extra={
              <Button type="link" size="small" style={{ padding: 0, fontSize: 11.5 }} onClick={() => navigate('/appeals')}>
                Все {totalAppeals} →
              </Button>
            }
          >
            {recentAppeals.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '28px 0', fontSize: 12.5, color: L.faint }}>
                Обращений пока нет
              </div>
            ) : (
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <tbody>
                  {recentAppeals.map((item, i) => (
                    <tr
                      key={i}
                      onClick={() => navigate('/appeals')}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = L.hover)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={{
                        padding: '9px 16px',
                        borderBottom: i < recentAppeals.length - 1 ? `1px solid ${L.lineSoft}` : 'none',
                      }}>
                        <div style={{ fontSize: 12.5, color: L.text }}>{item.title}</div>
                        <div style={{ fontSize: 11, color: L.faint, marginTop: 1 }}>
                          {[item.address, item.department].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td style={{
                        padding: '9px 16px',
                        borderBottom: i < recentAppeals.length - 1 ? `1px solid ${L.lineSoft}` : 'none',
                        whiteSpace: 'nowrap',
                        textAlign: 'right',
                      }}>
                        <StatusDot
                          color={STATUS_META[item.status]?.color ?? L.faint}
                          label={STATUS_META[item.status]?.label ?? item.status}
                        />
                        <span className="num" style={{ fontSize: 11, color: L.faint, marginLeft: 12 }}>
                          {dayjs(item.created_at).format('DD.MM')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Block>

          <Block title="Обращения по статусам">
            {statusEntries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '28px 0', fontSize: 12.5, color: L.faint }}>
                Нет данных
              </div>
            ) : (
              <div style={{ padding: '6px 0 8px' }}>
                {statusEntries.map(([status, count]) => {
                  const meta = STATUS_META[status];
                  const pct = totalAppeals > 0 ? Math.round((count / totalAppeals) * 100) : 0;
                  return (
                    <div
                      key={status}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 16px', fontSize: 12 }}
                    >
                      <StatusDot color={meta?.color ?? L.faint} label={meta?.label ?? status} />
                      <span style={{ flex: 1 }} />
                      <span style={{ width: 90, background: 'var(--l-fill)', borderRadius: 2, flex: 'none' }}>
                        <span style={{
                          display: 'block',
                          height: 4,
                          borderRadius: 2,
                          width: `${maxCount > 0 ? Math.round((count / maxCount) * 100) : 0}%`,
                          background: meta?.color ?? L.faint,
                        }} />
                      </span>
                      <span className="num" style={{ fontSize: 11.5, color: L.ink, width: 24, textAlign: 'right' }}>
                        {count}
                      </span>
                      <span className="num" style={{ fontSize: 11, color: L.faint, width: 32, textAlign: 'right' }}>
                        {pct}%
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Block>
        </div>
      </Spin>
    </div>
  );
};

export default Dashboard;
